import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Tooltip } from '@material-ui/core';
import clsx from 'clsx';
import { GameModes, ICloseCall, IDeath, IGame, IGamePlayer } from '@pvplevelup/core/dist/interfaces/Game/IGame';
import { isHealerClass } from '@pvplevelup/core/dist/pvp/ClassHp';
import { hhmmss } from '@pvplevelup/core/dist/utility/time';
import { TeamColors } from 'utilities/team_colors';

const GOOD = '#66bb6a';
const BAD = '#ef5350';
const HELP = '#42a5f5';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'grid',
    gridRowGap: theme.spacing(2),
    padding: theme.spacing(1, 0, 2),
  },
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gridGap: theme.spacing(1.5),
  },
  tile: {
    position: 'relative',
    overflow: 'hidden',
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 12,
    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  tileAccent: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
  },
  tileLabel: {
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    color: 'rgba(255, 255, 255, 0.5)',
  },
  tileValue: {
    marginTop: 2,
    fontSize: '1.6rem',
    fontWeight: 800,
    lineHeight: 1.15,
  },
  tileSub: {
    marginTop: 2,
    fontSize: '0.8rem',
    color: 'rgba(255, 255, 255, 0.6)',
  },
  groups: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gridGap: theme.spacing(2),
  },
  group: {
    padding: theme.spacing(1.5, 1.75, 1),
    borderRadius: 12,
    background: 'rgba(0, 0, 0, 0.18)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
  },
  groupTitle: {
    marginBottom: theme.spacing(1),
    fontSize: '0.78rem',
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'rgba(255, 255, 255, 0.75)',
  },
  stat: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridColumnGap: theme.spacing(1),
    alignItems: 'baseline',
    marginBottom: theme.spacing(1.1),
  },
  statName: {
    fontSize: '0.85rem',
    color: 'rgba(255, 255, 255, 0.8)',
  },
  statValue: {
    fontSize: '0.92rem',
    fontWeight: 800,
    fontVariantNumeric: 'tabular-nums',
  },
  statBar: {
    gridColumn: '1 / -1',
    position: 'relative',
    height: 5,
    marginTop: 4,
    borderRadius: 3,
    background: 'rgba(255, 255, 255, 0.07)',
    overflow: 'hidden',
  },
  statFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    borderRadius: 3,
  },
  rank: {
    marginLeft: 6,
    fontSize: '0.72rem',
    fontWeight: 700,
    color: 'rgba(255, 255, 255, 0.45)',
  },
  timeline: {
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 12,
    background: 'rgba(0, 0, 0, 0.18)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
  },
  track: {
    position: 'relative',
    height: 34,
    margin: theme.spacing(1, 0, 0.5),
    borderRadius: 6,
    background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.07))',
  },
  marker: {
    position: 'absolute',
    top: '50%',
    width: 12,
    height: 12,
    marginLeft: -6,
    marginTop: -6,
    borderRadius: '50%',
    cursor: 'default',
    border: '2px solid rgba(0, 0, 0, 0.6)',
    transition: 'transform 0.12s',
    '&:hover': { transform: 'scale(1.35)' },
  },
  markerDiamond: {
    borderRadius: 2,
    transform: 'rotate(45deg)',
    '&:hover': { transform: 'rotate(45deg) scale(1.35)' },
  },
  axis: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    fontSize: '0.72rem',
    color: 'rgba(255, 255, 255, 0.4)',
  },
  legend: {
    display: 'grid',
    gridAutoFlow: 'column',
    justifyContent: 'start',
    gridColumnGap: theme.spacing(2),
    alignItems: 'center',
    fontSize: '0.75rem',
    color: 'rgba(255, 255, 255, 0.6)',
  },
  legendDot: {
    display: 'inline-block',
    width: 9,
    height: 9,
    marginRight: 5,
    borderRadius: '50%',
    verticalAlign: 'middle',
  },
  tip: {
    maxWidth: 280,
    fontSize: '0.78rem',
    lineHeight: 1.45,
  },
  tipTitle: {
    marginBottom: 4,
    fontWeight: 800,
    fontSize: '0.85rem',
  },
  muted: {
    color: 'rgba(255, 255, 255, 0.55)',
  },
}));

interface Props {
  player: IGamePlayer;
  game: IGame;
}

const num = (n: number | null | undefined, digits = 0) => (n || 0).toLocaleString(undefined, {maximumFractionDigits: digits, minimumFractionDigits: digits});
const short = (n: number | null | undefined) => {
  const v = n || 0;
  return v >= 10000 ? `${(v / 1000).toFixed(v >= 100000 ? 0 : 1)}k` : num(v);
};
const pct = (part: number, whole: number) => whole > 0 ? Math.round(100 * part / whole) : 0;

interface StatDef {
  label: string;
  value: (p: IGamePlayer) => number;
  format?: (v: number, p: IGamePlayer) => string;
  /** lower is better (deaths): the bar and rank flip */
  lower?: boolean;
  hint: string;
}

/** The team play stats (games with trackingVersion 2): what a player did for their team, against their teammates. */
export const TeamplayPanel: React.FC<Props> = ({player, game}) => {
  const classes = useStyles();
  const team = (game.players || []).filter((p) => p.team == player.team && p.team != null && !p.kicked);
  const color = TeamColors[player.team || 0];
  const healer = isHealerClass(player.class);
  const ctf = game.mode == GameModes.CaptureTheFlag;
  const objective = game.mode == GameModes.KingoftheHill || game.mode == GameModes.Conquest;
  const deaths: IDeath[] = (game.deaths && game.deaths.length ? game.deaths : (game as any).info?.deaths) || [];
  const closeCalls: ICloseCall[] = (game as any).info?.closeCalls || [];
  const teamKills = (game.teams || []).find((t) => t.team == player.team)?.kills || team.reduce((s, p) => s + (p.kills || 0), 0);

  const statRow = (def: StatDef) => {
    const values = team.map((p) => def.value(p));
    const mine = def.value(player);
    const best = def.lower ? Math.min(...values) : Math.max(...values);
    const worst = def.lower ? Math.max(...values) : Math.min(...values);
    const rank = 1 + values.filter((v) => def.lower ? v < mine : v > mine).length;
    const fill = def.lower
      ? (worst > best ? (worst - mine) / (worst - best) : 1)
      : (best > 0 ? mine / best : 0);
    return (
      <Tooltip key={def.label} title={def.hint} placement="top-start" enterDelay={300}>
        <div className={classes.stat}>
          <span className={classes.statName}>{def.label}</span>
          <span className={classes.statValue}>
            {def.format ? def.format(mine, player) : num(mine)}
            {team.length > 1 && <span className={classes.rank}>#{rank}</span>}
          </span>
          <div className={classes.statBar}>
            <div className={classes.statFill} style={{width: `${Math.max(3, Math.round(fill * 100))}%`, background: `linear-gradient(90deg, ${color}aa, ${color})`}} />
          </div>
        </div>
      </Tooltip>
    );
  };

  const group = (title: string, defs: Array<StatDef | false>) => (
    <div className={classes.group}>
      <div className={classes.groupTitle}>{title}</div>
      {defs.filter((d): d is StatDef => !!d).map(statRow)}
    </div>
  );

  const alive = player.aliveTime || 0;
  const teamPressure = team.reduce((s, p) => s + (p.pressureAbsorbed || 0), 0);
  const myCloseCalls = closeCalls.filter((c) => c.victim?.name == player.name);
  const savedOthers = closeCalls.filter((c) => c.victim?.name != player.name && ((c.dCCs || []).some((x) => x.by == player.name) || (c.heals || []).some((x) => x.by == player.name)));

  const tile = (label: string, value: React.ReactNode, sub: React.ReactNode, accent: string) => (
    <div className={classes.tile}>
      <div className={classes.tileAccent} style={{background: accent}} />
      <div className={classes.tileLabel}>{label}</div>
      <div className={classes.tileValue}>{value}</div>
      <div className={classes.tileSub}>{sub}</div>
    </div>
  );

  // timeline: the player's deaths, the close calls they lived through and the ones they helped an ally survive
  const length = Math.max(1, game.timeElapsed || 1, ...deaths.map((d) => d.gtime || 0), ...closeCalls.map((c) => c.gtime || 0));
  const x = (gtime: number) => `${Math.min(100, Math.max(0, 100 * gtime / length))}%`;
  const myDeaths = deaths.filter((d) => d.victim?.name == player.name && d.gtime != null);
  const helpers = (list: Array<{by: string}>) => Array.from(new Set(list.map((h) => h.by)));

  return (
    <div className={classes.root}>
      <div className={classes.tiles}>
        {tile('Kill credit', `${pct(player.killCredit || 0, teamKills)}%`, `of the team's ${teamKills} kills (${num(player.killCredit, 1)} kills' worth)`, color)}
        {tile('Saves', num(player.saves), `close calls survived with your help; ${num(player.dCCs)} defensive CCs`, HELP)}
        {healer
          ? tile('Healing in danger', short(player.healInDanger), `on allies in danger; ${num(player.eBigHeal)} effective big heals`, GOOD)
          : tile('Pressure absorbed', short(player.pressureAbsorbed), `${pct(player.pressureAbsorbed || 0, teamPressure)}% of the team's, taken without dying`, GOOD)}
        {tile('Close calls', num(myCloseCalls.length), `survived; ${num(player.deaths)} deaths, ${num(player.diedFirst)} first in a fight`, myCloseCalls.length >= (player.deaths || 0) ? GOOD : BAD)}
      </div>

      <div className={classes.groups}>
        {group('Offense', [
          {label: 'Kill credit', value: (p) => p.killCredit || 0, format: (v) => `${pct(v, teamKills)}%`,
            hint: `Share of the team's kills: each kill is split among the attackers by their share of the victim's damage that life. ${num(player.killCredit, 1)} of ${teamKills} kills.`},
          {label: 'Offensive CCs', value: (p) => p.oCCs || 0, hint: 'Your crowd control on an enemy from 5 seconds before they died (up to 2 seconds after).'},
          {label: 'Healer pressure', value: (p) => p.healerPressure || 0, format: (v) => short(v), hint: 'Damage dealt to enemy healers.'},
          objective && {label: 'Objective kills', value: (p) => p.objectiveKills || 0, hint: 'Effective kills on enemies inside (or within 4 of) the objective.'},
        ])}
        {group('Support', [
          {label: 'Defensive CCs', value: (p) => p.dCCs || 0, hint: 'Crowd control on an enemy who was hitting an ally in danger (40% of their health in 5 seconds; a flat 4000 before season 6).'},
          {label: 'Saves', value: (p) => p.saves || 0, hint: 'Close calls you helped an ally survive, with a defensive CC on their attacker or a big heal.'},
          healer && {label: 'Healing in danger', value: (p) => p.healInDanger || 0, format: (v) => short(v), hint: 'All your healing on allies in danger, in a close call, or in the 8 seconds before they died.'},
          healer && {label: 'Effective big heals', value: (p) => p.eBigHeal || 0, hint: 'Big heals (Radiant Glow, Hibernation, Energized Soul) on an ally who took 40% of their health in 5 seconds (a flat 4000 before season 6).'},
        ])}
        {group('Survival', [
          {label: 'Pressure absorbed', value: (p) => p.pressureAbsorbed || 0, format: (v) => short(v), hint: 'Damage taken without dying from it (outside the 8 seconds before your deaths).'},
          {label: 'Died first in a fight', value: (p) => p.diedFirst || 0, lower: true, hint: 'Deaths that opened a fight: no death in the 20 seconds before.'},
          {label: 'Isolated deaths', value: (p) => p.isolatedDeaths || 0, lower: true, hint: 'Deaths with no ally within 20 (when their positions were known).'},
          {label: 'Back in the fight', value: (p) => (p.respawns || 0) > 0 ? (p.rejoinTime || 0) / (p.respawns || 1) : 0, lower: true, format: (v, p) => (p.respawns || 0) > 0 ? `${Math.round(v)}s` : '-', hint: 'Average time from a death back into combat.'},
        ])}
        {group('Presence', [
          {label: 'In combat', value: (p) => pct(p.activeTime || 0, p.aliveTime || 0), format: (v) => `${v}%`, hint: 'Share of your time alive spent dealing, taking or healing damage.'},
          {label: 'Near an ally', value: (p) => pct(p.cohesionTime || 0, p.aliveTime || 0), format: (v) => `${v}%`, hint: 'Share of your time alive with a teammate within 20.'},
          (player.timeInObjective || 0) > 0 && {label: 'Time on the objective', value: (p) => p.timeInObjective || 0, format: (v) => hhmmss(v, false), hint: 'Time inside the hill or capture points.'},
          ctf && {label: 'Flag carries', value: (p) => p.flagCarries || 0, hint: 'Times you picked up the flag.'},
          ctf && {label: 'Flag captures', value: (p) => p.flagCaptures || 0, hint: 'Flags you brought home.'},
          ctf && {label: 'Flag returns', value: (p) => p.flagReturns || 0, hint: 'Dropped flags you guarded until they reset.'},
        ])}
      </div>

      <div className={classes.timeline}>
        <div className={classes.groupTitle} style={{marginBottom: 0}}>Timeline</div>
        <div className={classes.track}>
          {savedOthers.map((c, i) => (
            <Tooltip key={`s${i}`} classes={{tooltip: classes.tip}} title={
              <React.Fragment>
                <div className={classes.tipTitle}>You helped {c.victim?.name} survive</div>
                <div>{short(c.damage)} damage taken, {short(c.healed)} healed over {Math.round(c.duration)}s</div>
                {(c.dCCs || []).filter((x) => x.by == player.name).map((x, j) => <div key={j} className={classes.muted}>{x.ability.replace(' Immunity', '')} on {x.on}</div>)}
                {(c.heals || []).filter((x) => x.by == player.name).map((x, j) => <div key={`h${j}`} className={classes.muted}>{x.count > 1 ? `${x.count} big heals` : 'Big heal'}</div>)}
              </React.Fragment>
            }>
              <div className={clsx(classes.marker, classes.markerDiamond)} style={{left: x(c.gtime), background: HELP}} />
            </Tooltip>
          ))}
          {myCloseCalls.map((c, i) => (
            <Tooltip key={`c${i}`} classes={{tooltip: classes.tip}} title={
              <React.Fragment>
                <div className={classes.tipTitle}>Close call at {hhmmss(c.gtime, false)}</div>
                <div>{short(c.damage)} damage taken, {short(c.healed)} healed over {Math.round(c.duration)}s</div>
                {helpers([...(c.dCCs || []), ...(c.heals || [])]).length > 0
                  ? <div className={classes.muted}>Helped by {helpers([...(c.dCCs || []), ...(c.heals || [])]).join(', ')}</div>
                  : <div className={classes.muted}>No help from allies</div>}
              </React.Fragment>
            }>
              <div className={classes.marker} style={{left: x(c.gtime), background: GOOD}} />
            </Tooltip>
          ))}
          {myDeaths.map((d, i) => {
            const saves = d.saves;
            const tried = saves ? helpers([...(saves.dCCs || []), ...(saves.heals || [])]) : [];
            return (
              <Tooltip key={`d${i}`} classes={{tooltip: classes.tip}} title={
                <React.Fragment>
                  <div className={classes.tipTitle}>Died at {hhmmss(d.gtime || 0, false)}{d.diedFirst ? ' (first in the fight)' : ''}</div>
                  <div>Killed by {d.eKill || d.killed_by?.name || 'unknown'}{d.isolated ? ', away from the team' : ''}{d.inObjective ? `, on ${d.inObjective}` : ''}</div>
                  {saves && (saves.oCCs || []).length > 0 && <div className={classes.muted}>CC'd {(saves.oCCs || []).length}x in the last 8s</div>}
                  <div className={classes.muted}>{tried.length > 0 ? `Allies tried to save you: ${tried.join(', ')}` : 'No ally tried to save you'}</div>
                </React.Fragment>
              }>
                <div className={classes.marker} style={{left: x(d.gtime || 0), background: BAD}} />
              </Tooltip>
            );
          })}
        </div>
        <div className={classes.axis}>
          <span>0:00</span><span />
          <span>{hhmmss(length, false)}</span>
        </div>
        <div className={classes.legend} style={{marginTop: 6}}>
          <span><span className={classes.legendDot} style={{background: BAD}} />Death</span>
          <span><span className={classes.legendDot} style={{background: GOOD}} />Close call survived</span>
          <span><span className={classes.legendDot} style={{background: HELP, borderRadius: 2}} />Helped an ally survive</span>
        </div>
      </div>
    </div>
  );
};
